import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Alert,
} from "@mui/material";
import { Lock, Visibility, VisibilityOff, Login } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import CustomDialogTitle from "../CustomDialogTitle";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSetPasswordMutation, useResetPasswordUserMutation } from "../../../store/api/usersApiSlice";
import { toast } from "react-toastify";
import { appColors } from "../../../constants/theme";

const FirstLoginPasswordResetDialog = ({ open, onClose, onSuccess, userEmail, userType }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [setPassword, { isLoading: isSetPasswordLoading }] = useSetPasswordMutation();
  const [resetPasswordUser, { isLoading: isResetPasswordLoading }] = useResetPasswordUserMutation();
  const isLoading = isSetPasswordLoading || isResetPasswordLoading;
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Assistant için sadece email ve newPassword gönderilmeli
  const isAssistant = userType?.toUpperCase() === 'ASSISTANT';

  const validationSchema = Yup.object({
    password: Yup.string()
      .required(t("required_field"))
      .min(8, t("register_password_limit", { item: 8 }))
      .max(32, t("register_password_max_limit", { item: 32 }))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/, t("error_generic_password_rules")),
    confirmPassword: Yup.string()
      .required(t("required_field"))
      .max(32, t("register_password_max_limit", { item: 32 }))
      .oneOf([Yup.ref("password"), null], t("error_generic_password_mismatch")),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setErrorMessage(null);
      try {
        const result = isAssistant
          ? await resetPasswordUser({
              email: userEmail,
              newPassword: values.password,
            }).unwrap()
          : await setPassword({
              newPassword: values.password,
              confirmPassword: values.confirmPassword,
            }).unwrap();

        if (result.isSuccess) {
          if (onSuccess) onSuccess();
        } else {
          const errorMsg = result.message || t("password_reset_failed");
          // TODO: hata mesajı geri açılacaktır.
          // setErrorMessage(errorMsg);
          // toast.error(errorMsg);
          setErrorMessage(t("error_generic_action_failed"));
          toast.error(t("error_generic_action_failed"));
        }
      } catch (error) {
        console.error("Reset password error:", error);
        
        // Handle validation errors from backend
        if (error.status === 400 && error.data?.errors) {
            const errorMessages = Object.values(error.data.errors).flat().join(", ");
            // TODO: hata mesajı geri açılacaktır.
            // setErrorMessage(errorMessages || t("password_reset_failed"));
            // toast.error(errorMessages || t("password_reset_failed"));
            setErrorMessage(t("error_generic_action_failed"));
            toast.error(t("error_generic_action_failed"));
        } else {
            const errorMsg = error?.data?.message || t("password_reset_failed");
            // TODO: hata mesajı geri açılacaktır.
            // setErrorMessage(errorMsg);
            // toast.error(errorMsg);
            setErrorMessage(t("error_generic_action_failed"));
            toast.error(t("error_generic_action_failed"));
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing by clicking outside or pressing escape
        // Force user to either reset password or click Cancel (which logs out)
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <CustomDialogTitle
        title={t("first_login_password_reset_title")}
        type="warning"
        onClose={onClose}
      />
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <Box
              sx={{
                p: 2,
                backgroundColor: appColors.warningBg,
                borderRadius: 2,
                border: `1px solid ${appColors.warning}`,
              }}
            >
              <Typography variant="body1" sx={{ color: appColors.warningDark, fontWeight: 500 }}>
                {t("first_login_password_reset_message")}
              </Typography>
            </Box>

            {errorMessage && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {errorMessage}
              </Alert>
            )}

            <TextField
              fullWidth
              name="password"
              label={t("new_password")}
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: appColors.gray500 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="confirmPassword"
              label={t("confirm_password")}
              type={showConfirmPassword ? "text" : "password"}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: appColors.gray500 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 2 }}>
          <Button
            onClick={onClose}
            color="inherit"
            disabled={isLoading}
          >
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Login />}
            disabled={isLoading || !formik.isValid}
            sx={{
              textTransform: "none",
              minWidth: 150,
            }}
          >
            {isLoading ? t("saving") : t("save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FirstLoginPasswordResetDialog;
